function getTriplePixelFn() {
    if (typeof window === 'undefined') {
        return null
    }

    const triplePixelFn = window.TriplePixel
    if (typeof triplePixelFn !== 'function') {
        return null
    }

    return triplePixelFn
}

function safeString(value) {
    if (value === null || value === undefined) {
        return null
    }

    const trimmed = String(value).trim()
    return trimmed ? trimmed : null
}

function formatAmount(value) {
    if (value === null || value === undefined) {
        return null
    }

    const numeric = Number(value)
    if (Number.isFinite(numeric)) {
        // Preserve decimals if present, but avoid unnecessary trailing zeros.
        return String(numeric)
    }

    const str = safeString(value)
    return str
}

function formatGiftCardLabelForTripleWhale({ amount, merchantName, kind } = {}) {
    const amountString = formatAmount(amount)
    if (!amountString) {
        return null
    }

    const merchant = safeString(merchantName)
    const suffix = safeString(kind) ? ` ${safeString(kind)}` : ' Gift Card'

    if (merchant) {
        return `$${amountString} ${merchant}${suffix}`
    }
    return `$${amountString}${suffix}`
}

export function fireTriplePixelContact({ email, phone } = {}) {
    const triplePixelFn = getTriplePixelFn()
    if (!triplePixelFn) {
        return false
    }

    const safeEmail = safeString(email)
    const safePhone = safeString(phone)
    if (!safeEmail && !safePhone) {
        return false
    }

    const payload = {}
    if (safeEmail) {
        payload.email = safeEmail
    }
    if (safePhone) {
        payload.phone = safePhone
    }

    try {
        triplePixelFn('Contact', payload)
    } catch (e) {
        return false
    }

    return true
}

export function fireTriplePixelAddToCart({ item, q, v } = {}) {
    const triplePixelFn = getTriplePixelFn()
    if (!triplePixelFn) {
        return false
    }

    const safeItem = safeString(item)
    const quantity = Number(q)
    const safeValue = safeString(v)

    if (!safeItem || !Number.isFinite(quantity) || quantity <= 0 || !safeValue) {
        return false
    }

    try {
        triplePixelFn('AddToCart', {
            item: safeItem,
            q: quantity,
            v: safeValue,
        })
    } catch (e) {
        return false
    }

    return true
}

export function buildTriplePixelLineItemsFromGiftCards(items) {
    if (!Array.isArray(items) || items.length === 0) {
        return []
    }

    const lineItemByLabel = new Map()

    items.forEach((item) => {
        const label = formatGiftCardLabelForTripleWhale(item)
        if (!label) {
            return
        }

        const existing = lineItemByLabel.get(label)
        if (existing) {
            existing.q += 1
            return
        }

        lineItemByLabel.set(label, {
            i: label,
            q: 1,
            v: label,
        })
    })

    return Array.from(lineItemByLabel.values())
}

export function fireTriplePixelPurchase({ email, phone, orderId, lineItems } = {}) {
    const triplePixelFn = getTriplePixelFn()
    if (!triplePixelFn) {
        return false
    }

    const safeOrderId = safeString(orderId)
    if (!safeOrderId) {
        return false
    }

    const safeEmail = safeString(email)
    const safePhone = safeString(phone)
    if (!safeEmail && !safePhone) {
        return false
    }

    if (!Array.isArray(lineItems) || lineItems.length === 0) {
        return false
    }

    const payload = {
        orderId: safeOrderId,
        lineItems,
    }
    if (safeEmail) {
        payload.email = safeEmail
    }
    if (safePhone) {
        payload.phone = safePhone
    }

    try {
        triplePixelFn('Purchase', payload)
    } catch (e) {
        return false
    }

    return true
}

export function fireTriplePixelAddToCartForGiftCards(cards, { kind } = {}) {
    if (!Array.isArray(cards) || cards.length === 0) {
        return false
    }

    const lineItems = buildTriplePixelLineItemsFromGiftCards(
        cards.map((c) => ({
            amount: c?.amount,
            merchantName: c?.merchantName,
            kind,
        }))
    )

    if (!lineItems.length) {
        return false
    }

    // TriplePixel AddToCart accepts one item per call.
    return lineItems
        .map((li) => fireTriplePixelAddToCart({ item: li.i, q: li.q, v: li.v }))
        .some(Boolean)
}
